import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Fuel, Settings, Users, Gauge, MapPin } from "lucide-react";
import { dkk, plain, cloudinaryThumb, hasUploadedPhotos } from "../../lib/publicUtils";

export default function CarCard({ car, type = "rent", mode = null }) {
  const photos = car.public_photos || [];
  const hasPhotos = hasUploadedPhotos(car);
  const hasMultiple = photos.length > 1;
  const [idx, setIdx] = useState(0);

  const img = hasPhotos ? cloudinaryThumb(photos[idx].url) : null;

  function nav(e, dir) {
    e.preventDefault();
    e.stopPropagation();
    setIdx((i) => (i + dir + photos.length) % photos.length);
  }

  const to = type === "rent" ? `/biludlejning/${car.license_plate}` : `/til-salg/${car.license_plate}`;
  const isLager = car.status === "lager";
  const isCarbuddi = type === "rent" && !car.is_gomore_public;
  const isRentedOut = type === "rent" && car.is_rented_out;
  const isAvailable = isCarbuddi && !isRentedOut && !isLager;
  const returnDateFmt = isRentedOut && car.rental_return_date
    ? (() => { const [y, m, d] = car.rental_return_date.split("-"); return `${d}.${m}.${y}`; })()
    : null;
  return (
    <Link
      to={to}
      className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-transparent hover:border-[#E5DED1] block ${isRentedOut ? "opacity-95" : ""}`}
      data-testid={`car-card-${car.license_plate}`}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-[#F1EBE1]">
        {isRentedOut && (
          <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2 z-10">
            <span className="bg-red-600 text-white px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-lg" data-testid={`card-rented-${car.license_plate}`}>
              ● Udlejet
            </span>
            {returnDateFmt && (
              <span className="bg-white/95 text-graphite px-3 py-1.5 rounded-full text-[10px] font-semibold shadow-md">
                Retur {returnDateFmt}
              </span>
            )}
          </div>
        )}
        {isAvailable && (
          <div className="absolute top-3 left-3 z-10">
            <span className="bg-emerald-500 text-white px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-lg inline-flex items-center gap-1.5" data-testid={`card-available-${car.license_plate}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/> Ledig
            </span>
          </div>
        )}
        {hasPhotos ? (
          <img
            src={img}
            alt={`${car.make} ${car.model}`}
            className={`object-cover w-full h-full transform transition-transform duration-700 ${isRentedOut ? "grayscale-[35%] group-hover:grayscale-0" : "group-hover:scale-105"}`}
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-[#F1EBE1]" data-testid={`card-no-photos-${car.license_plate}`}>
            <div className="text-center px-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-graphite/40 mb-1">{car.make} {car.model}</div>
              <div className="font-heading text-base sm:text-lg text-graphite/65">Billeder på vej</div>
            </div>
          </div>
        )}
        {hasMultiple && (
          <>
            <button
              type="button"
              onClick={(e) => nav(e, -1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white text-graphite shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              aria-label="Forrige billede"
              data-testid={`card-prev-${car.license_plate}`}
            >
              <ChevronLeft size={18} strokeWidth={2}/>
            </button>
            <button
              type="button"
              onClick={(e) => nav(e, 1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white text-graphite shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              aria-label="Næste billede"
              data-testid={`card-next-${car.license_plate}`}
            >
              <ChevronRight size={18} strokeWidth={2}/>
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              {photos.map((_, i) => (
                <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-white" : "bg-white/40"}`}/>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            {car.category && (
              <span className="bg-[#F7F3EC] text-graphite-light px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider">
                {car.category}
              </span>
            )}
            {type === "rent" && car.status === "lager" && (
              <span className="bg-copper text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" data-testid={`card-coming-${car.license_plate}`}>
                Snart tilgængelig
              </span>
            )}
            {type === "rent" && car.status !== "lager" && car.is_gomore_public && (
              <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" data-testid={`card-gomore-${car.license_plate}`}>
                Dags leje
              </span>
            )}
            {type === "rent" && car.status !== "lager" && !car.is_gomore_public && (
              <span className="bg-graphite text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" data-testid={`card-longterm-${car.license_plate}`}>
                Langtid leje
              </span>
            )}
            {type === "sale" && car.sale_type === "engros" && (
              <span className="bg-graphite text-copper px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider" data-testid={`card-saletype-${car.license_plate}`}>
                Engros
              </span>
            )}
            {type === "sale" && car.sale_type === "formidling" && (
              <span className="bg-copper/15 text-copper-dark px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider" data-testid={`card-saletype-${car.license_plate}`}>
                Formidling
              </span>
            )}
          </div>
          {car.year && <span className="text-xs text-graphite/50 shrink-0">{car.year}</span>}
        </div>
        <h3 className="font-heading text-xl sm:text-2xl text-graphite mb-1 leading-tight">
          {car.make} {car.model}
        </h3>
        {car.variant && <p className="text-xs text-graphite/50">{car.variant}</p>}
        {type === "rent" && car.public_location ? (
          <p className="text-xs text-graphite/60 mt-1 mb-4 inline-flex items-center gap-1" data-testid={`card-location-${car.license_plate}`}>
            <MapPin size={11} strokeWidth={1.7}/> {car.public_location}
          </p>
        ) : (
          <div className="mb-4" />
        )}
        <div className="flex items-center gap-3 text-[11px] text-graphite/60 mb-4 flex-wrap">
          {car.fuel_type && (<span className="inline-flex items-center gap-1"><Fuel size={12} strokeWidth={1.5}/>{car.fuel_type}</span>)}
          {car.transmission && (<span className="inline-flex items-center gap-1"><Settings size={12} strokeWidth={1.5}/>{car.transmission}</span>)}
          {car.seats && (<span className="inline-flex items-center gap-1"><Users size={12} strokeWidth={1.5}/>{car.seats} sæder</span>)}
          {type === "sale" && Number.isFinite(car.current_km) && (
            <span className="inline-flex items-center gap-1 font-medium text-graphite/80" data-testid={`card-km-${car.license_plate}`}>
              <Gauge size={12} strokeWidth={1.5}/>{plain(car.current_km)} km
            </span>
          )}
        </div>
        <div className="flex items-baseline justify-between border-t border-[#E5DED1] pt-4">
          {type === "rent" ? (
            <>
              <div>
                {car.is_gomore_public ? (
                  <>
                    <div className="font-heading text-base text-graphite/80" data-testid={`card-gomore-price-${car.license_plate}`}>
                      Se pris på GoMore
                    </div>
                    <div className="text-[10px] text-graphite/50 uppercase tracking-wider">via ekstern booking</div>
                  </>
                ) : car.sub_min_price ? (
                  <>
                    <div className="font-heading text-2xl text-graphite" data-testid={`card-from-${car.license_plate}`}>
                      Fra {dkk(car.sub_min_price)}
                    </div>
                    <div className="text-[10px] text-graphite/50 uppercase tracking-wider">
                      pr. md.{car.sub_min_binding ? ` · ved ${car.sub_min_binding} mdr.` : ""}
                    </div>
                  </>
                ) : (
                  <div className="font-heading text-base text-graphite/80">Pris ved henvendelse</div>
                )}
              </div>
              <span className="text-sm text-copper font-medium group-hover:underline">Se bil →</span>
            </>
          ) : (
            <>
              <div>
                {car.sale_price ? (
                  <>
                    <div className="font-heading text-2xl text-graphite">{dkk(car.sale_price)}</div>
                    <div className="text-[10px] text-graphite/50 uppercase tracking-wider">salgspris</div>
                  </>
                ) : (
                  <div className="font-heading text-base text-graphite/80">Pris ved henvendelse</div>
                )}
              </div>
              <span className="text-sm text-copper font-medium group-hover:underline">Læs mere →</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
